import React, { useState, useEffect, useCallback } from "react";
import { Autocomplete, useLoadScript, Libraries } from "@react-google-maps/api";
import { LocationIcon, CloseCircleIcon } from "../Icons";
import { Input, Spin, Button } from "antd";
import { useUserLocation } from "./LocationContext";
import { formatCityName, getCanonicalCitySlug } from "../../config/cityData";

const GOOGLE_MAPS_LIBRARIES: Libraries = ["places"];
const MAP_SCRIPT_ID = "turfbooking-google-maps-places";

export interface LocationPlacePayload {
  latitude: number;
  longitude: number;
  name: string;
  address: string;
  city?: string;
}

interface LocationAutocompleteProps {
  onPlaceSelected: (place: LocationPlacePayload | null) => void;
  /** Current app city slug — used for search placeholder only */
  citySlugHint?: string | null;
}

function extractCitySlugFromPlaceResult(
  place: google.maps.places.PlaceResult
): string | undefined {
  const components = place.address_components;
  if (!components?.length) return undefined;
  const locality = components.find((c) => c.types.includes("locality"));
  const adm2 = components.find((c) =>
    c.types.includes("administrative_area_level_2")
  );
  const name = locality?.long_name || adm2?.long_name;
  if (!name) return undefined;
  return getCanonicalCitySlug(name);
}

interface InnerProps extends LocationAutocompleteProps {
  apiKey: string;
  onRetryLoad: () => void;
}

const LocationAutocompleteLoaded: React.FC<InnerProps> = ({
  onPlaceSelected,
  citySlugHint,
  apiKey,
  onRetryLoad,
}) => {
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [selectedLocation, setSelectedLocation] = useState("");
  const { userLocation, setUserLocation } = useUserLocation();

  const { isLoaded, loadError } = useLoadScript({
    id: MAP_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (autocomplete) {
      autocomplete.setFields(["geometry", "name", "formatted_address", "address_component"]);
    }
  }, [autocomplete]);

  const handlePlaceSelect = useCallback(() => {
    if (autocomplete === null) return;
    const place = autocomplete.getPlace();
    const newLocation = place.name || place.formatted_address || "";
    setSelectedLocation(newLocation);
    const citySlug = extractCitySlugFromPlaceResult(place);
    onPlaceSelected({
      latitude: place.geometry?.location?.lat() || 0,
      longitude: place.geometry?.location?.lng() || 0,
      name: place.name || "",
      address: place.formatted_address || "",
      city: citySlug,
    });
  }, [autocomplete, onPlaceSelected]);

  const handleClearLocation = useCallback(() => {
    setSelectedLocation("");
    setUserLocation(null);
  }, [setUserLocation]);

  const hintCity =
    citySlugHint && citySlugHint.length > 0
      ? formatCityName(citySlugHint)
      : null;
  const placeholder = hintCity
    ? `Search area, landmark in ${hintCity}`
    : userLocation?.name || "Search area, society, landmark…";

  if (loadError) {
    return (
      <div className="location-maps-status location-maps-status--error">
        <p className="location-maps-status__title">Could not load maps</p>
        <p className="location-maps-status__detail">
          Check your connection or Google Maps API key, then try again.
        </p>
        <Button type="primary" onClick={onRetryLoad} block>
          Retry
        </Button>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="location-maps-status location-maps-status--loading">
        <Input
          disabled
          size="large"
          placeholder="Loading maps search…"
          prefix={<LocationIcon size={16} color="#483285" />}
          className="location-maps-status__input"
        />
        <div className="location-maps-status__spin">
          <Spin size="small" />
          <span>Loading location search</span>
        </div>
      </div>
    );
  }

  return (
    <Autocomplete
      onLoad={(auto) => setAutocomplete(auto)}
      onPlaceChanged={handlePlaceSelect}
      className="custom-autocomplete"
      restrictions={{ country: "in" }}
      types={["geocode"]}
    >
      <Input
        size="large"
        prefix={<LocationIcon size={16} color="#483285" />}
        suffix={
          selectedLocation ? (
            <CloseCircleIcon size={14} onClick={handleClearLocation} />
          ) : undefined
        }
        placeholder={placeholder}
        value={selectedLocation}
        onChange={(e) => setSelectedLocation(e.target.value)}
        allowClear={false}
      />
    </Autocomplete>
  );
};

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  onPlaceSelected,
  citySlugHint,
}) => {
  const [loadAttempt, setLoadAttempt] = useState(0);
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY?.trim() || "";

  const onRetryLoad = useCallback(() => {
    setLoadAttempt((n) => n + 1);
  }, []);

  if (!apiKey) {
    return (
      <div className="location-maps-status location-maps-status--error">
        <p className="location-maps-status__title">Location search unavailable</p>
        <p className="location-maps-status__detail">
          Maps API key is not configured. Use &quot;Detect current location&quot;
          below or contact support.
        </p>
      </div>
    );
  }

  return (
    <LocationAutocompleteLoaded
      key={loadAttempt}
      apiKey={apiKey}
      onRetryLoad={onRetryLoad}
      onPlaceSelected={onPlaceSelected}
      citySlugHint={citySlugHint}
    />
  );
};

export default LocationAutocomplete;
